import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
  scopeBind,
} from 'effector'
import type Keycloak from 'keycloak-js'

import AuthManager, {
  type KeycloakHookCallbacks,
} from '../../api/auth/auth-manager'
import { type UserRolesEnum, scope } from '../../config'
import { MS_IN_SECOND } from '../../lib/constants'

// --- Effector события, строго типизированные под keycloak-js хуки ---
export const sessionAuthSuccess = createEvent('Session auth success')
export const sessionAuthError =
  createEvent<Parameters<NonNullable<Keycloak['onAuthError']>>[0]>(
    'Session auth error',
  )
export const sessionAuthLogout = createEvent('Session logout')
export const sessionTokenExpired = createEvent('Session token expired')
export const sessionActionUpdate = createEvent<
  Parameters<NonNullable<Keycloak['onActionUpdate']>>[0]
>('Session action update')
export const sessionAuthRefreshSuccess = createEvent(
  'Session auth refresh success',
)
export const sessionAuthRefreshError = createEvent('Session auth refresh error')
export const sessionReady =
  createEvent<Parameters<NonNullable<Keycloak['onReady']>>[0]>('Session ready')

const hookCallbacks: KeycloakHookCallbacks = {
  onActionUpdate: scopeBind(sessionActionUpdate, { scope }),
  onAuthError: scopeBind(sessionAuthError, { scope }),
  onAuthLogout: scopeBind(sessionAuthLogout, { scope }),
  onAuthRefreshError: scopeBind(sessionAuthRefreshError, { scope }),
  onAuthRefreshSuccess: scopeBind(sessionAuthRefreshSuccess, { scope }),
  onAuthSuccess: scopeBind(sessionAuthSuccess, { scope }),
  onReady: scopeBind(sessionReady, { scope }),
  onTokenExpired: scopeBind(sessionTokenExpired, { scope }),
}

// --- Эффекты для управления Keycloak ---
export const initSessionFx = createEffect(() => {
  void AuthManager.initKeycloak(hookCallbacks)
})

export const loginRequested = createEvent()
export const logoutRequested = createEvent()
export const scheduleRefreshTokenRequested =
  createEvent<Keycloak['tokenParsed']>()

export const loginFx = createEffect(() => AuthManager.login())
export const logoutFx = createEffect(() => AuthManager.logout())
export const refreshTokenFx = createEffect(async () => {
  const refreshedToken = await AuthManager.refreshToken()
  if (!refreshedToken) throw new Error('Failed to refresh token')
  return refreshedToken
})

let refreshTimeout: ReturnType<typeof setTimeout> | null = null
const clearRefreshTimeout = () => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout)
    refreshTimeout = null
  }
}

export const scheduleRefreshTokenFx = createEffect(
  (exp: number | undefined) => {
    if (!exp) return
    clearRefreshTimeout()

    const now = Date.now()
    const timeSkew = (AuthManager.getTimeSkew?.() ?? 0) * MS_IN_SECOND
    const refreshAt =
      exp * MS_IN_SECOND -
      timeSkew -
      AuthManager.MIN_TOKEN_VALIDITY_TIME_IN_SECONDS * MS_IN_SECOND

    const delay = Math.max(0, refreshAt - now)

    if (delay <= 0) {
      void refreshTokenFx()
      return
    }

    refreshTimeout = setTimeout(() => {
      refreshTimeout = null
      void refreshTokenFx()
    }, delay)
  },
)
// --- Stores ---
export const $sessionPending = createStore<boolean>(true)
  .on(initSessionFx, () => true)
  .on(sessionAuthSuccess, () => false)
  .on(sessionAuthLogout, () => true)
  .on(sessionAuthError, () => false)

export const $sessionToken = createStore<Keycloak['token']>(undefined, {
  skipVoid: false,
})
  .on(sessionAuthSuccess, () => AuthManager.getToken())
  .on(sessionAuthRefreshSuccess, () => AuthManager.getToken())
  .reset(sessionAuthLogout)

export const $sessionTokenParsed = createStore<Keycloak['tokenParsed']>(
  undefined,
  { skipVoid: false },
)
  .on(sessionAuthSuccess, () => AuthManager.getTokenParsed())
  .on(sessionAuthRefreshSuccess, () => AuthManager.getTokenParsed())
  .reset(sessionAuthLogout)

export const $sessionUsername = createStore<string | undefined>(undefined, {
  skipVoid: false,
})
  .on(sessionAuthSuccess, () => AuthManager.getUsername())
  .on(sessionAuthRefreshSuccess, () => AuthManager.getUsername())
  .reset(sessionAuthLogout)

export const $sessionRole = createStore<UserRolesEnum | undefined>(undefined, {
  skipVoid: false,
})
  .on(sessionAuthSuccess, () => AuthManager.getRole())
  .on(sessionAuthRefreshSuccess, () => AuthManager.getRole())
  .reset(sessionAuthLogout)

export const $sessionIsAuthenticated = createStore<boolean>(false)
  .on(sessionAuthSuccess, () => true)
  .on(sessionAuthLogout, () => false)
  .reset(sessionAuthLogout)

// --- helper fx для доступа к токену ---
export const getTokenFx = attach({
  source: $sessionToken,
  effect: (token) => {
    return token ?? ''
  },
})

// --- связи ---
sample({
  clock: loginRequested,
  source: {
    initPending: initSessionFx.pending,
    isAuthenticated: $sessionIsAuthenticated,
  },
  filter: ({ initPending, isAuthenticated }) =>
    !initPending && !isAuthenticated,
  target: loginFx,
})

sample({
  clock: [sessionAuthSuccess, sessionAuthRefreshSuccess],
  source: $sessionTokenParsed,
  filter: (parsed): parsed is NonNullable<typeof parsed> =>
    Boolean(parsed?.exp),
  target: scheduleRefreshTokenRequested,
})

sample({
  clock: scheduleRefreshTokenRequested,
  fn: (tokenParsed) => tokenParsed?.exp,
  target: scheduleRefreshTokenFx,
})

sample({
  clock: sessionTokenExpired,
  target: refreshTokenFx,
})

sample({
  clock: logoutRequested,
  target: logoutFx,
})
